/**
 * Per-provider permission contracts shown in the consent modal before OAuth.
 *
 * The "reads" / "writes" arrays should describe in plain user-language what
 * Orbit will actually do — these are the promises we make at consent. Keep
 * them short, concrete, and never aspirational.
 *
 * "neverDoes" is the trust shelf — what we explicitly will NOT do regardless
 * of scope. This is product policy, not technical limitation. Phase 1 of
 * Agents Anywhere commits to read-first, no destructive operations during
 * onboarding.
 */

export interface PermissionContract {
  displayName: string;
  /** One-line plain-language description shown under the title. */
  caption: string;
  /** Brand color for accent. */
  accent: string;
  /** What Orbit will read from the provider (data direction: provider → Orbit). */
  reads: string[];
  /** What Orbit will write back. Empty = read-only. */
  writes?: string[];
  /** Hard guarantees — never executed even if technically allowed by scope. */
  neverDoes: string[];
  /** OAuth scope hint shown in the small print. */
  scopeHint?: string;
}

const COMMON_NEVER_DOES = [
  'Delete anything',
  'Share your data with other Orbit customers',
  'Use your data to train models',
];

export const PROVIDER_PERMISSIONS: Record<string, PermissionContract> = {
  gmail: {
    displayName: 'Gmail',
    caption: 'Read your sent + inbox metadata to learn how you communicate.',
    accent: '#EA4335',
    reads: [
      'Subject lines, recipients, and timestamps from sent messages',
      'Full message bodies for emails you explicitly summarize',
      'Folder & label structure',
    ],
    writes: ['Draft emails when you ask — saved to Drafts, never auto-sent'],
    neverDoes: ['Send email on your behalf without explicit confirmation', ...COMMON_NEVER_DOES],
    scopeHint: 'gmail.readonly + gmail.compose (drafts only)',
  },
  google_calendar: {
    displayName: 'Google Calendar',
    caption: 'See your schedule to prep meetings and find open focus blocks.',
    accent: '#4285F4',
    reads: [
      'Events, attendees, locations, and meeting descriptions',
      'Calendar lists you own or are subscribed to',
    ],
    writes: ['Create events when you explicitly ask'],
    neverDoes: ['Auto-decline or move meetings', ...COMMON_NEVER_DOES],
    scopeHint: 'calendar.readonly + calendar.events',
  },
  google_drive: {
    displayName: 'Google Drive',
    caption: 'Index your docs so Orbit can find and reference them.',
    accent: '#34A853',
    reads: [
      'File names, types, owners, and recent activity',
      'Document content for files you point Orbit to',
    ],
    neverDoes: ['Read every file in your Drive automatically', 'Modify file permissions', ...COMMON_NEVER_DOES],
    scopeHint: 'drive.metadata.readonly + drive.file (per-document)',
  },
  slack: {
    displayName: 'Slack',
    caption: 'Read messages in channels you\'re in to summarize and search.',
    accent: '#4A154B',
    reads: [
      'Messages in channels you\'re a member of',
      'Direct messages where you explicitly ask Orbit to look',
      'Channel membership and user directory (names, titles)',
    ],
    writes: ['Post a message when you explicitly ask'],
    neverDoes: ['Auto-post in any channel', 'Read DMs you don\'t reference', ...COMMON_NEVER_DOES],
    scopeHint: 'channels:read + chat:write (manual only)',
  },
  github: {
    displayName: 'GitHub',
    caption: 'See your repos, PRs, and issues to surface what needs attention.',
    accent: '#181717',
    reads: [
      'Repositories you own or have access to',
      'Pull requests, issues, and review status',
      'Commit history and PR comments',
    ],
    writes: ['Comment on PRs when you explicitly ask'],
    neverDoes: ['Push code', 'Merge PRs', 'Create or modify branches', ...COMMON_NEVER_DOES],
    scopeHint: 'repo (read) + write:discussion (comments only)',
  },
  hubspot: {
    displayName: 'HubSpot',
    caption: 'See contacts, companies, and deals so Orbit knows your pipeline.',
    accent: '#FF7A59',
    reads: [
      'Contacts, companies, and deals',
      'Activity history (emails, calls, meetings)',
      'Pipeline stages and deal owners',
    ],
    writes: ['Log notes & tasks when you ask'],
    neverDoes: ['Move deals between stages', 'Delete records', ...COMMON_NEVER_DOES],
    scopeHint: 'crm.objects.* (read) + crm.notes.write',
  },
  stripe: {
    displayName: 'Stripe',
    caption: 'See payments, subscriptions, and customers for revenue analysis.',
    accent: '#635BFF',
    reads: [
      'Charges, payments, and refunds',
      'Subscriptions and customer records',
      'Invoices and payouts',
    ],
    neverDoes: ['Issue refunds', 'Create or modify customers', 'Trigger any charges', ...COMMON_NEVER_DOES],
    scopeHint: 'read_only API key',
  },
  shopify: {
    displayName: 'Shopify',
    caption: 'See products, orders, and inventory.',
    accent: '#96BF48',
    reads: [
      'Product catalog and variants',
      'Recent orders and customers',
      'Inventory levels',
    ],
    neverDoes: ['Create or modify products', 'Issue refunds', 'Change inventory', ...COMMON_NEVER_DOES],
    scopeHint: 'read_products + read_orders + read_inventory',
  },
  notion: {
    displayName: 'Notion',
    caption: 'See pages and databases you grant access to.',
    accent: '#000000',
    reads: ['Pages and database content from spaces you grant'],
    writes: ['Append content to pages when you ask'],
    neverDoes: ['Read pages you didn\'t share', 'Delete pages', ...COMMON_NEVER_DOES],
    scopeHint: 'notion.read + notion.append (per-page consent)',
  },
  linear: {
    displayName: 'Linear',
    caption: 'See issues, projects, and cycles assigned to your team.',
    accent: '#5E6AD2',
    reads: ['Issues, projects, cycles, and team membership', 'Comments and activity'],
    writes: ['Create or comment on issues when you ask'],
    neverDoes: ['Close or delete issues', ...COMMON_NEVER_DOES],
  },
};

/** Sane fallback for providers we haven't enumerated yet. */
export function getPermissionContract(provider: string): PermissionContract {
  const key = provider.toLowerCase();
  if (PROVIDER_PERMISSIONS[key]) return PROVIDER_PERMISSIONS[key];
  return {
    displayName: provider.charAt(0).toUpperCase() + provider.slice(1),
    caption: 'Read access so Orbit can work with your data.',
    accent: '#18191d',
    reads: ['Data you authorize during the OAuth flow'],
    neverDoes: ['Take destructive actions without explicit confirmation', ...COMMON_NEVER_DOES],
  };
}
